import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { EASE, useScrollTo, preselectBuilding, OPS_OPTION, openSchedule } from "./Reveal";

const Line = ({ children, delay }) => (
  <span className="mask-line">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
      className="block"
    >
      {children}
    </motion.span>
  </span>
);

const Node = ({ x, y, label, delay }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.6 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay, duration: 0.8, ease: EASE }}
    className="absolute hidden md:flex items-center gap-2"
    style={{ left: x, top: y }}
  >
    <span className="w-2 h-2 rounded-full bg-skope shadow-[0_0_12px_rgba(56,182,255,0.8)]" />
    <span className="font-mono text-[10px] tracking-[0.25em] text-ink/50 uppercase whitespace-nowrap">{label}</span>
  </motion.div>
);

const Hero = () => {
  const ref = useRef(null);
  const scrollTo = useScrollTo();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "32%"]);
  const tiltX = useMotionValue(0);
  const tiltY = useMotionValue(0);
  const rotateX = useSpring(tiltX, { stiffness: 120, damping: 18 });
  const rotateY = useSpring(tiltY, { stiffness: 120, damping: 18 });

  const handleTilt = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    tiltX.set(((e.clientY - r.top) / r.height - 0.5) * -8);
    tiltY.set(((e.clientX - r.left) / r.width - 0.5) * 10);
  };
  const resetTilt = () => { tiltX.set(0); tiltY.set(0); };

  return (
    <section ref={ref} id="top" className="relative min-h-screen bg-white bg-blueprint overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-transparent to-white pointer-events-none" />

      {/* network lines */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true">
        <motion.line x1="8%" y1="78%" x2="46%" y2="30%" stroke="#38B6FF" strokeOpacity="0.18" strokeWidth="1"
          initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2, delay: 1.2 }} />
        <motion.line x1="46%" y1="30%" x2="88%" y2="62%" stroke="#38B6FF" strokeOpacity="0.18" strokeWidth="1"
          initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2, delay: 1.5 }} />
        <motion.circle cx="46%" cy="30%" r="90" fill="none" stroke="#38B6FF" strokeOpacity="0.12" strokeWidth="1"
          initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2.4, delay: 1 }} />
      </svg>
      <Node x="8%" y="76%" label="Concept" delay={1.4} />
      <Node x="44%" y="27%" label="Kitchen" delay={1.7} />
      <Node x="86%" y="59%" label="Scale" delay={2} />

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 pt-32 lg:pt-40 pb-24 grid lg:grid-cols-12 gap-12 items-center min-h-screen">
        <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="flex items-center gap-3 font-mono text-[11px] tracking-[0.4em] text-skope uppercase mb-8"
            data-testid="hero-label"
          >
            <span className="w-8 h-px bg-skope" />
            Skope Kitchens
          </motion.div>

          <h1 className="font-display font-bold text-[13vw] sm:text-6xl lg:text-7xl xl:text-[5.4rem] leading-[0.98] tracking-tighter text-ink" data-testid="hero-headline">
            <Line delay={0.45}>BUILDING</Line>
            <Line delay={0.58}><span className="text-skope">ICONIC</span> FOOD</Line>
            <Line delay={0.71}>BRANDS</Line>
            <Line delay={0.84}>PEOPLE <span className="text-outline">LOVE.</span></Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.9, ease: EASE }}
            className="mt-8 max-w-xl text-ink/60 text-base lg:text-lg leading-relaxed"
            data-testid="hero-subcopy"
          >
            The easiest way to start in the food industry. We help food entrepreneurs turn
            ideas into commercially viable food businesses — from concept and menu to kitchen,
            operations and scale.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.9, ease: EASE }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <button
              onClick={() => openSchedule()}
              className="group flex items-center justify-center gap-3 bg-skope text-ink font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-ink hover:text-white transition-colors duration-300"
              data-testid="hero-cta-schedule"
            >
              Schedule a Call
              <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
            <button
              onClick={() => scrollTo("#calculator")}
              className="flex items-center justify-center gap-3 border border-ink/25 text-ink font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 hover:border-skope hover:text-skope transition-colors duration-300"
              data-testid="hero-cta-calculator"
            >
              Calculate Your Kitchen
            </button>
          </motion.div>

          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.55, duration: 0.9 }}
            onClick={() => { preselectBuilding(OPS_OPTION); scrollTo("#contact"); }}
            className="mt-6 flex items-center gap-2 font-mono text-[11px] tracking-[0.15em] uppercase text-ink/45 hover:text-skope transition-colors duration-300 underline-offset-4 hover:underline"
            data-testid="hero-cta-handover"
          >
            Struggling to run your kitchen? Hand it to us <ArrowRight size={13} />
          </motion.button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.9, duration: 1.2, ease: EASE }}
          onMouseMove={handleTilt}
          onMouseLeave={resetTilt}
          style={{ rotateX, rotateY, transformPerspective: 900 }}
          className="lg:col-span-5 relative hidden lg:block"
          data-testid="hero-visual"
        >
          <div className="relative border border-ink/10">
            <motion.div style={{ y: imgY }} className="overflow-hidden">
              <img
                src="/assets/img/hero-kitchen.jpg"
                alt="Chef preparing food in a premium stainless steel commercial kitchen"
                className="w-full h-[520px] object-cover scale-110"
              />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-t from-white/70 via-transparent to-transparent" />
            <div className="absolute -left-px -top-px w-6 h-6 border-l-2 border-t-2 border-skope" />
            <div className="absolute -right-px -bottom-px w-6 h-6 border-r-2 border-b-2 border-skope" />
            <div className="absolute bottom-5 left-5 right-5 flex justify-between items-end">
              <div className="font-mono text-[10px] tracking-[0.25em] text-ink/70 uppercase">
                Fig. 01 — The Operating System<br />Behind The Brand
              </div>
              <div className="font-mono text-[10px] text-skope">[ K-01 ]</div>
            </div>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.6, duration: 0.8, ease: EASE }}
            className="absolute -bottom-8 -left-10 bg-[#f6f6f6] border border-ink/10 px-6 py-4 backdrop-blur-xl"
          >
            <div className="font-mono text-[10px] tracking-[0.25em] text-ink/40 uppercase">Concept → Kitchen → Scale</div>
            <div className="font-display text-ink font-semibold mt-1">One connected system.</div>
          </motion.div>
        </motion.div>
      </div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2 }}
        onClick={() => scrollTo("#big-idea")}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-ink/40 hover:text-skope transition-colors"
        aria-label="Scroll down"
        data-testid="hero-scroll-indicator"
      >
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={14} />
        </motion.span>
      </motion.button>
    </section>
  );
};

export default Hero;
